import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiBaseUrl, REGION_CONFIG } from '../config/region';
import { useUserStore } from '../stores/useUserStore';

const AuthContext = createContext(null);

// v68.394 — keep the app-v2 zustand store (what MeScreen / the avatar read) in
// lockstep with AuthContext. Before this, AuthContext NEVER touched useUserStore,
// so after an email-OTP login the new "Me" screen kept showing whatever stale
// identity was persisted from a previous Google login (Patrick logged in as the
// patt@ alias but saw poskar74@gmail.com). The App.jsx bridge only hydrated
// the store once on mount and short-circuited on a stale avatar, so a re-login
// in the same session never propagated. Syncing at the auth source fixes it for
// every login method (phone OTP, email OTP, init-from-token).
const syncToUserStore = (userData) => {
  try {
    const token = localStorage.getItem('venue_app_token') || localStorage.getItem('token') || null;
    if (userData && token) {
      useUserStore.getState().setUser({
        id: userData.id || userData.userId,
        userId: userData.userId || userData.id,
        email: userData.email || null,
        name: userData.name || null,
        phone: userData.phone || null,
        role: userData.role || null,
        region: userData.region || null,
        avatar_url: userData.avatar_url || userData.profileImage || null,
        isCurator: userData.isCurator || false,   // EV-588
      }, token);
    } else {
      useUserStore.getState().clearUser();
    }
  } catch { /* zustand sync is best-effort */ }
};

// Helper: Hash token for cache binding
const hashToken = (token) => {
  if (!token) return null;
  // Simple hash for cache key (not cryptographic)
  let hash = 0;
  for (let i = 0; i < token.length; i++) {
    const char = token.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(16);
};

// Helper: Clear ALL auth-related localStorage
const clearAllAuthData = (source) => {
  console.log(`🧹 [${source}] Clearing ALL auth data from localStorage`);
  
  const keysToRemove = [
    'venue_app_token', 'token',
    'user_email', 'userEmail',
    'user_name', 'userName',
    'user_phone', 'userPhone',
    'user_id', 'userId',
    'user_role', 'userRole',
    'user_region', 'userRegion',
    'user_login_method', 'loginMethod',
    'user_qr_token', 'qr_token',
    'utm_campaign', 'utm_source', 'utm_data',
    'cachedUser', 'cachedUserTokenHash'
  ];
  
  keysToRemove.forEach(key => localStorage.removeItem(key));
  // v68.394 — also clear the app-v2 zustand store so a stale persisted identity
  // (e.g. an earlier Google login as poskar74) can NEVER survive into a new login.
  try { useUserStore.getState().clearUser(); } catch { /* */ }
  console.log(`✅ [${source}] Cleared ${keysToRemove.length} localStorage keys`);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Single source of truth: fetch user from /api/users/me
  const fetchUserFromServer = async (token) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      console.log('🔍 [AuthContext] Fetching user from /api/users/me');
      
      // ✅ CRITICAL: Use credentials: 'omit' to prevent sending refresh token cookie
      // This ensures /api/users/me ONLY uses Authorization header (access token)
      const response = await fetch(`${apiBaseUrl}/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-region': REGION_CONFIG.region
        },
        credentials: 'omit' // ✅ Do NOT send cookies (prevents refresh token confusion)
      });

      if (!response.ok) {
        console.error(`❌ [AuthContext] /api/users/me failed: ${response.status}`);
        return null;
      }

      const data = await response.json();
      console.log('✅ [AuthContext] User fetched from server:', data.email || data.phone);
      
      return {
        id: data._id || data.id,
        userId: data._id || data.id,
        email: data.email,
        name: data.name,
        phone: data.phone,
        role: data.role,
        region: data.region || REGION_CONFIG.region,
        profileImage: data.profileImage,
        loginMethod: data.loginMethod,
        isCurator: data.isCurator || false   // EV-588
      };
    } catch (error) {
      console.error('❌ [AuthContext] Error fetching user:', error);
      return null;
    }
  };

  // Initialize auth on mount
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🚀 [AuthContext] Initializing auth...');
      
      try {
        const token = localStorage.getItem('venue_app_token') || 
                      localStorage.getItem('token');
        
        if (!token) {
          console.log('ℹ️  [AuthContext] No token found, user not authenticated');
          setUser(null);
          setLoading(false);
          return;
        }

        console.log('🔑 [AuthContext] Token found, fetching user from server...');

        // ✅ ALWAYS fetch from server when token exists (no localStorage hydration)
        const userData = await fetchUserFromServer(token);
        
        if (userData) {
          console.log('✅ [AuthContext:INIT] setUser from server');
          setUser(userData);
          syncToUserStore(userData);   // v68.394 keep app-v2 MeScreen identity in sync
        } else {
          console.log('❌ [AuthContext] Failed to fetch user, clearing auth');
          clearAllAuthData('INIT_FAILED');
          setUser(null);
        }
      } catch (error) {
        console.error('❌ [AuthContext] Auth initialization error:', error);
        clearAllAuthData('INIT_ERROR');
        setUser(null);
      } finally {
        setLoading(false);
        console.log('✅ [AuthContext] Auth initialization complete');
      }
    };

    initializeAuth();
  }, []);

  // Send OTP
  const sendOTP = async (phoneData) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      console.log('📱 [AuthContext] Sending OTP to:', phoneData.phone);
      
      const response = await fetch(`${apiBaseUrl}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phoneData.phone,
          region: phoneData.region || REGION_CONFIG.region
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to send OTP');
      }

      console.log('✅ [AuthContext] OTP sent successfully');
      return { success: true, message: data.message };
    } catch (error) {
      console.error('❌ [AuthContext] Send OTP error:', error);
      return { success: false, error: error.message };
    }
  };

  // Verify OTP
  const verifyOTP = async (otpData) => {
    try {
      setLoading(true);
      const apiBaseUrl = getApiBaseUrl();
      console.log('🔐 [AuthContext] Verifying OTP for:', otpData.phone);
      
      // ✅ STEP 1: Clear stale cache BEFORE login
      clearAllAuthData('BEFORE_OTP_LOGIN');

      const response = await fetch(`${apiBaseUrl}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          phone: otpData.phone,
          otp: otpData.otp,
          region: otpData.region || REGION_CONFIG.region
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Invalid OTP');
      }

      console.log('✅ [AuthContext] OTP verified, token received');

      // ✅ STEP 2: Store ONLY token
      localStorage.setItem('venue_app_token', data.token);
      localStorage.setItem('token', data.token);

      // ✅ STEP 3: Fetch user from server
      const userData = await fetchUserFromServer(data.token);
      
      if (userData) {
        console.log('✅ [AuthContext:OTP_LOGIN] setUser from server');
        setUser(userData);
        syncToUserStore(userData);   // v68.394 keep app-v2 MeScreen identity in sync
        return { success: true };
      } else {
        throw new Error('Failed to fetch user data after login');
      }
    } catch (error) {
      console.error('❌ [AuthContext] Verify OTP error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Send Email OTP
  const sendEmailOTP = async (emailData) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      console.log('📧 [AuthContext] Sending email OTP to:', emailData.email);
      
      const response = await fetch(`${apiBaseUrl}/auth/send-email-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailData.email,
          region: emailData.region || REGION_CONFIG.region
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to send email OTP');
      }

      console.log('✅ [AuthContext] Email OTP sent successfully');
      return { success: true, message: data.message, data: data };
    } catch (error) {
      console.error('❌ [AuthContext] Send email OTP error:', error);
      return { success: false, error: error.message };
    }
  };

  // Verify Email OTP
  const verifyEmailOTP = async (otpData) => {
    try {
      setLoading(true);
      const apiBaseUrl = getApiBaseUrl();
      console.log('🔐 [AuthContext] Verifying email OTP for:', otpData.email);
      
      // ✅ STEP 1: Clear stale cache BEFORE login
      clearAllAuthData('BEFORE_EMAIL_LOGIN');

      const response = await fetch(`${apiBaseUrl}/auth/verify-email-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: otpData.email,
          otp: otpData.otp,
          region: otpData.region || REGION_CONFIG.region
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Invalid OTP');
      }

      console.log('✅ [AuthContext] Email OTP verified, token received');

      // ✅ STEP 2: Store ONLY token
      localStorage.setItem('venue_app_token', data.token);
      localStorage.setItem('token', data.token);

      // ✅ STEP 3: Fetch user from server
      const userData = await fetchUserFromServer(data.token);
      
      if (userData) {
        console.log('✅ [AuthContext:EMAIL_LOGIN] setUser from server');
        setUser(userData);
        syncToUserStore(userData);   // v68.394 keep app-v2 MeScreen identity in sync
        return { success: true };
      } else {
        throw new Error('Failed to fetch user data after login');
      }
    } catch (error) {
      console.error('❌ [AuthContext] Verify email OTP error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = async () => {
    console.log('👋 [AuthContext] Logging out user');
    
    try {
      const apiBaseUrl = getApiBaseUrl();
      
      // Call backend logout (revokes refresh token)
      await fetch(`${apiBaseUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include' // Important for cookie
      });
      
      console.log('✅ [AuthContext] Backend logout successful');
    } catch (error) {
      console.warn('⚠️  [AuthContext] Backend logout failed (non-critical):', error.message);
    }
    
    // Clear all local data
    console.log('✅ [AuthContext:LOGOUT] setUser(null)');
    setUser(null);
    clearAllAuthData('LOGOUT');
  };

  const value = {
    user,
    sendOTP,
    verifyOTP,
    sendEmailOTP,
    verifyEmailOTP,
    logout,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
